import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import EmailIcon from '@birdi/icons/onboarding-at.svg';
import InboxIcon from '@birdi/icons/mail.svg';
// @ts-ignore - JS file without types
import { getVerificationToken, updateField } from '../actions';
import VerifyYourEmail from './verify-your-email';
import { ProgressBar } from './progress-bar';
import * as styles from './styles';
import { AuthState } from '../reducer';

interface ResendVerificationProps {
  auth: AuthState;
  _updateField: (field: Partial<AuthState>) => void;
  _getVerificationToken: (email: string) => Promise<any>;
}

const ResendVerification: React.FC<ResendVerificationProps> = (props) => {
  const theme = useTheme() as BirdiTheme;
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  // Reset awaitingEmailVerification when component mounts so user can see the form
  useEffect(() => {
    if (props.auth.awaitingEmailVerification) {
      props._updateField({ awaitingEmailVerification: false });
    }
  }, []);

  const handleGetVerificationToken = () => {
    props
      ._getVerificationToken(props.auth.email)
      .then(() => {
        setEmailSent(true);
        props._updateField({ awaitingEmailVerification: true });
      })
      .catch((err: any) => {
        if (err?.body?.message === 'userAlreadyVerified') {
          setError('This email has already been verified. Please login.');
        } else {
          setError(
            'We can’t find that email address in our records. If the issue persists, please contact support@birdi.io',
          );
        }
      });
  };

  if (emailSent || props.auth.awaitingEmailVerification) {
    return <VerifyYourEmail />;
  }

  return (
    <>
      {/* Logo - Fixed at top */}
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      {/* Scrollable Content */}
      <div css={styles.scrollableContent}>
        {/* Header Section */}
        <div css={styles.headerSection}>
          <div css={styles.iconContainer}>
            <div css={styles.iconWrapper(theme)}>
              <InboxIcon />
            </div>
            <h1 css={styles.title}>Resend verification email</h1>
          </div>

          <ProgressBar currentStep={2} totalSteps={6} />
        </div>

        {/* Form */}
        <form
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            @media (min-width: ${breakpoints.s}) {
              gap: 0.5rem;
            }
          `}
          onSubmit={(event) => {
            event.preventDefault();
            handleGetVerificationToken();
          }}
        >
        <p
          css={(theme: BirdiTheme) => css`
            font-size: 0.9375rem;
            color: ${theme.mono70};
            margin: 0 0 1rem 0;
            line-height: 1.5;
          `}
        >
          Enter your email where we will send your email verification link to:
        </p>

        <div css={styles.formField}>
          <label css={styles.label} htmlFor="email">
            Email <span className="required">*</span>
          </label>
          <div css={styles.inputWrapper}>
            <div css={styles.inputIcon}>
              <EmailIcon />
            </div>
            <input
              id="email"
              type="email"
              css={styles.input}
              placeholder="Enter your email"
              value={props.auth.email}
              onChange={(event) =>
                props._updateField({ email: event.target.value })
              }
              required
            />
          </div>
        </div>

        {error && (
          <div
            css={css`
              ${styles.errorMessage};
              margin-top: 0.5rem;
            `}
          >
            {error}
          </div>
        )}

        <div css={styles.buttonContainer}>
          <button type="submit" css={styles.primaryButton}>
            Resend verification link
          </button>
        </div>
      </form>
      </div>

      {/* Fixed Footer - Help Link */}
      <div css={styles.fixedFooter}>
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: any) => ({
    auth: state.auth,
  }),
  (dispatch: any) => ({
    _getVerificationToken: (email: string) =>
      dispatch(getVerificationToken(email)),
    _updateField: (field: Partial<AuthState>) => dispatch(updateField(field)),
  }),
)(ResendVerification);

